import { useState } from 'react';
import {
  deletePhotos,
  listPhotos,
  photoContentTypeOf,
  sha256Hex,
  uploadPhotos,
  type PhotoContentType,
} from '../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../api/hooks.js';
import {
  Async,
  Badge,
  Button,
  Failure,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

export function PhotosScreen() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const uploadKey = useIntentKey();

  // Protection is decided by the image-worker, so the list is polled while any
  // photo is still unprotected with no reason recorded. That transport is not
  // wired yet, so expect it to stay pending on dev rather than flip.
  const photos = useApi(() => listPhotos(cursor), [cursor], { pollMs: 10_000 });

  const upload = useAction(
    async (files: File[], key: string) => {
      const accepted: { file: File; sha256: string; contentType: PhotoContentType }[] = [];
      const refused: string[] = [];
      for (const file of files) {
        const contentType = photoContentTypeOf(file);
        if (contentType === null) {
          refused.push(`${file.name} — unsupported type`);
          continue;
        }
        accepted.push({ file, sha256: await sha256Hex(file), contentType });
      }
      setRejected(refused);
      if (accepted.length === 0) throw new Error('no supported files selected');
      return uploadPhotos(accepted, key, (done, total) => setProgress(`${done}/${total} uploaded`));
    },
    () => {
      uploadKey.next();
      setProgress(null);
      photos.reload();
    },
  );

  const remove = useAction(deletePhotos, () => {
    setSelected(new Set());
    photos.reload();
  });

  const toggle = (photoId: string) => {
    const next = new Set(selected);
    if (next.has(photoId)) next.delete(photoId);
    else next.add(photoId);
    setSelected(next);
  };

  return (
    <Screen
      title="Photos"
      lede="Mint presigned PUTs, upload straight to S3, then commit. No route on the api ever carries image bytes — the commit body is hashes and ids."
    >
      <Note>
        The browser PUT is cross-origin by definition, so it needs CORS on{' '}
        <code>imageshield-dev-uploads</code> allowing <code>PUT</code> from this origin
        (DEPLOY-DEV.md §7.2). And <code>protected</code> flips only once the image-worker has
        processed the file — that transport is unwired, so expect it to stay <code>false</code> on
        dev.
      </Note>

      <Panel title="upload">
        <Row>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/heic,image/heif,.jpg,.jpeg,.heic,.heif"
            onChange={(event) => {
              const files = [...(event.target.files ?? [])];
              if (files.length > 0) upload.run(files, uploadKey.key);
            }}
            disabled={upload.pending}
          />
        </Row>
        <p className="muted" style={{ marginTop: 8 }}>
          SHA-256 is computed here and validated as 64 lowercase hex by the api — which proves the
          value is well-formed, not that it describes the bytes. The image-worker computes the real
          one. Commit key: <code>{uploadKey.key}</code>
        </p>
        {progress !== null && <p className="muted">{progress}</p>}
        {rejected.length > 0 && (
          <p className="muted">
            skipped: {rejected.join(', ')}
          </p>
        )}
        {upload.error !== null && (
          <Failure
            error={upload.error}
            hint={
              {
                FILE_TOO_LARGE: 'Above the per-file ceiling in config.',
                LIMIT_EXCEEDED: 'Too many files for one request, or the account’s photo ceiling.',
                UPLOAD_NOT_FOUND: 'A minted upload expired before the commit — mint again.',
                S3_TRANSPORT: 'The bucket almost certainly does not allow PUT from this origin yet.',
                S3_REJECTED:
                  'S3 refused the signed PUT. A SignatureDoesNotMatch here usually means the Content-Type sent differs from the one used when minting.',
              }[upload.error.code]
            }
          />
        )}
        {upload.result !== null && <Raw label="commit response" value={upload.result} />}
      </Panel>

      <Panel title="your photos">
        <Async state={photos} empty="no photos yet">
          {(data) => (
            <>
              {data.photos.length === 0 ? (
                <p className="empty">no photos yet — 200 with an empty list, not a 404</p>
              ) : (
                <div className="thumbs">
                  {data.photos.map((photo) => (
                    <div className="thumb" key={photo.photo_id}>
                      <img src={photo.url} alt="" loading="lazy" />
                      <div className="meta">
                        <div>
                          {photo.protected ? (
                            <Badge tone="ok">protected</Badge>
                          ) : (
                            <Badge tone="warn">unprotected</Badge>
                          )}
                        </div>
                        {photo.unprotected_reason !== null && (
                          <div className="muted">{photo.unprotected_reason}</div>
                        )}
                        {/* FACES, NOT PEOPLE (2026-08-31). Monitoring is per
                            covered face, and these two numbers are counts of
                            DETECTED FACES — which is not the same claim as a
                            count of humans present. Rekognition returns a box
                            per detection: a face on a poster, a reflection, or
                            one person picked up twice all add to faces_total.
                            Saying "3 people in this photo" asserts something
                            about who is in the room that we did not measure and
                            cannot stand behind; "3 faces detected" is exactly
                            what we did measure. The wording mattered while the
                            counts were also inflated (0025) — a solo selfie read
                            "monitoring 3 of 3 people" — and it would still be
                            the wrong noun with the counts correct. */}
                        <div className="muted">
                          {photo.faces_total === 0
                            ? 'no faces detected'
                            : photo.faces_covered === 0
                              ? `nobody monitored yet (${String(photo.faces_total)} ${photo.faces_total === 1 ? 'face' : 'faces'} detected)`
                              : `monitoring ${String(photo.faces_covered)} of ${String(photo.faces_total)} ${photo.faces_total === 1 ? 'face' : 'faces'} detected`}
                        </div>
                        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selected.has(photo.photo_id)}
                            onChange={() => toggle(photo.photo_id)}
                          />
                          select
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Row>
                <Button
                  variant="danger"
                  onClick={() => remove.run([...selected])}
                  disabled={remove.pending || selected.size === 0}
                >
                  {remove.pending ? 'deleting…' : `Delete ${selected.size} selected`}
                </Button>
                {data.next_cursor !== null && (
                  <Button variant="secondary" onClick={() => setCursor(data.next_cursor ?? undefined)}>
                    Next page
                  </Button>
                )}
                {cursor !== undefined && (
                  <Button variant="secondary" onClick={() => setCursor(undefined)}>
                    First page
                  </Button>
                )}
              </Row>
              <p className="muted" style={{ marginTop: 8 }}>
                Delete answers 204 regardless of how many ids matched — a count would tell the caller
                which of the ids they sent were real.
              </p>
              {remove.error !== null && <Failure error={remove.error} />}
              <Raw label="GET /v1/photos" value={data} />
            </>
          )}
        </Async>
      </Panel>
    </Screen>
  );
}
